import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, Trash2, Loader2 } from 'lucide-react';
import { listRuns, deleteRun } from '../api';
import { TIER_STYLES } from '../constants';

export default function RunHistoryList({ onOpen }) {
    const qc = useQueryClient();

    const { data, isLoading, error } = useQuery({
        queryKey: ['profitabilityRuns'],
        queryFn: async () => {
            const res = await listRuns();
            if (!res.ok) throw new Error('Failed to load runs');
            return res.json();
        },
    });

    const { mutate: remove } = useMutation({
        mutationFn: (id) => deleteRun(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['profitabilityRuns'] }),
    });

    if (isLoading) {
        return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="animate-spin" size={16} /> Loading history…</div>;
    }
    if (error) return <div className="text-sm text-red-600">Couldn't load run history.</div>;

    const runs = data?.runs || [];
    if (runs.length === 0) {
        return <div className="text-sm text-slate-500 dark:text-slate-400">No saved runs yet. Completed scores appear here for reopen.</div>;
    }

    return (
        <div className="space-y-2">
            {runs.map(r => {
                const tierStyle = TIER_STYLES[r.tier] || TIER_STYLES['Watch'];
                return (
                    <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-400 transition-colors">
                        <button onClick={() => onOpen?.(r.id)} className="flex-1 text-left">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-slate-800 dark:text-slate-100">
                                    {r.property_name || r.property_id || 'Unnamed property'}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${tierStyle.bg} ${tierStyle.fg}`}>
                                    {r.jpro ?? '—'} · {r.tier}
                                </span>
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2">
                                <Clock size={12} />
                                {new Date(r.created_at).toLocaleString()}
                                {r.address && <span>· {r.address}</span>}
                                {r.confidence && <span>· {r.confidence}</span>}
                            </div>
                        </button>
                        <button onClick={() => { if (confirm('Delete this run?')) remove(r.id); }}
                                className="text-slate-400 hover:text-red-600" title="Delete">
                            <Trash2 size={16} />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
